import { Form, Input, Button, Typography } from 'antd';
import { User, Lock, Eye, EyeOff } from 'lucide-react';
import type { FormProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useLogin } from '../../hooks/useAuth';
import { tokenManager } from '../../api/tokenManager';
import { useAppStore } from '../../store/appStore';
import { App as AntdApp } from 'antd';
import CryptoJS from "crypto-js";
import { useEffect, useState } from 'react';
import axios from 'axios';
import { decryptData } from '../../utils/decrypt';

const { Link } = Typography;

// Interface for form values
interface LoginFormValues {
  email: string;
  password: string;
  deviceType: string;
  captchaToken:any
}

const Login = () => {
  const [form] = Form.useForm<LoginFormValues>();
  const navigate = useNavigate();
  const setProfile = useAppStore((state) => state.setProfile);
  const app = AntdApp.useApp();
  const [captchaToken, setCaptchaToken] = useState("");

  const { mutateAsync: login, isPending } = useLogin();


  const encryptPassword = (password: string) => {
  const secretKey ="aSFGBTYP&&&&WQE123OHNDJHT" // must match backend
  return CryptoJS.AES.encrypt(password, secretKey).toString();
};

useEffect(() => {
  const getCaptcha = async () => {
    const res = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/meeting-request/captcha-key`);

    const decrypted = decryptData(res.data, res.data.iv);

    setCaptchaToken(decrypted.token);
  };

  getCaptcha();
}, []);

  const onFinish: FormProps<LoginFormValues>['onFinish'] = async (values) => {
    if (!values) return;

    try {
      const encryptedPassword = encryptPassword(values.password);
      const res = await login({ ...values, 
        password: encryptedPassword,
        deviceType: 'web',
        captchaToken: captchaToken
     });
      setProfile(res);
      tokenManager.set('accessToken', res.accessToken, 15 * 60 * 1000);
      tokenManager.set('refreshToken', res.refreshToken, 7 * 24 * 60 * 60 * 1000);

      app.message.success('Login successful!');

      // Add a small delay to ensure state updates are processed
      setTimeout(() => {
        navigate('/dashboard');
      }, 100);
    } catch (error) {
     console.error('Login error:', error);

  const msg =
    error?.message ||
    error?.error ||
    error?.response?.data?.message ||
    'Login failed';

  app.message.error(msg);
    }
  };

  const emailRules = [
    {
      required: true,
      message: 'Please input your email!',
    },
    {
      type: 'email' as const,
      message: 'Please enter a valid email address!',
    },
  ];

  // Password validation rules
  const passwordRules = [
    {
      required: true,
      message: 'Please input your password!',
    },
    {
      min: 5,
      message: 'Password must be at least 8 characters long!',
    },
  ];

  const handleForgotPassword = (): void => {
    navigate('/forgot-password');
  };
  return (
    <div className="mt-8">
      {/* <input
  type="text"
  name="website"
  style={{ display: "none" }}
  autoComplete="off"
/> */}
      <Form<LoginFormValues>
        form={form}
        name="loginForm"
        onFinish={onFinish}
        // onFinishFailed={onFinishFailed}
        autoComplete="off"
        layout="vertical"
        size="large"
        validateTrigger="onBlur"
      >
        <Form.Item<LoginFormValues>
          label={<span className="text-sm font-medium text-gray-700">Email Address</span>}
          name="email"
          rules={emailRules}
          hasFeedback
        >
          <Input
            prefix={<User size={16} className="text-gray-400" />}
            placeholder="Enter your email address"
            className="rounded-lg h-12 text-base hover:border-blue-400 focus:border-blue-500"
            type="email"
          />
        </Form.Item>

        <Form.Item<LoginFormValues>
          label={<span className="text-sm font-medium text-gray-700">Password</span>}
          name="password"
          rules={passwordRules}
          hasFeedback
        >
          <Input.Password
            prefix={<Lock size={16} className="text-gray-400" />}
            placeholder="Enter your password"
            iconRender={(visible: boolean) =>
              visible ? (
                <Eye size={16} className="text-gray-400 hover:text-gray-600 cursor-pointer" />
              ) : (
                <EyeOff size={16} className="text-gray-400 hover:text-gray-600 cursor-pointer" />
              )
            }
            className="rounded-lg h-12 text-base hover:border-blue-400 focus:border-blue-500"
          />
        </Form.Item>

        <Form.Item className="mb-5">
          <div className="text-right">
            <Link
              onClick={handleForgotPassword}
              className="text-sm font-medium !text-[#fc4c02]  hover:!text-[#fc4c02]  cursor-pointer transition-colors duration-200"
            >
              Forgot password?
            </Link>
          </div>
        </Form.Item>

        <Form.Item className="mb-6">
          <Button
            type="primary"
            htmlType="submit"
            loading={isPending}
            block
            className="h-12 rounded-lg text-base font-semibold bg-blue-500 hover:bg-blue-600 border-blue-500 hover:border-blue-600 transition-all duration-200 shadow-md hover:shadow-lg"
            disabled={isPending}
          >
            {isPending ? 'Signing In...' : 'Sign In'}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default Login;
